<template>
  <div class="matrix-grid-table-component">
    <div
      class="matrix-grid-table"
      :class="{ answered }"
    >
      <div class="matrix-grid-row header">
        <div
          v-for="header in headers"
          :key="header"
          class="matrix-grid-cell"
        >
          <p class="cell-text">
            {{ header }}
          </p>
        </div>
      </div>
      <div
        v-for="answer in answers"
        :key="`table-${answer.id}`"
        class="matrix-grid-row"
      >
        <div class="matrix-grid-cell">
          <p class="cell-text">
            {{ answer.text }}
          </p>
        </div>
        <div
          v-for="(_, selectionIndex) in columns"
          :key="`table-${answer.id}-${selectionIndex}`"
          class="matrix-grid-cell"
        >
          <matrix-grid-input
            :answer="answer"
            :answered="answered"
            :correct="isSelectionCorrect(answer.id, selectionIndex)"
            :incorrect="isSelectionIncorrect(answer.id, selectionIndex)"
            :input-id="'table'"
            :input-type="inputType"
            :selected="isSelectionSelected(answer.id, selectionIndex)"
            :selection-index="selectionIndex"
            @handle-answer="handleAnswer"
          />
        </div>
      </div>
    </div>
    <div
      v-if="answered"
      class="matrix-grid-table-answer-rationales"
    >
      <div
        v-for="answer in answers"
        :key="answer.id"
        class="answer-rationale-container"
      >
        <div
          class="answer-icon-container"
          :class="{ correct: isAnswerCorrect(answer.id) }"
        >
          <icon
            class="answer-icon"
            :name="answerIconName(answer.id)"
            variant="white"
          />
        </div>
        <p class="answer-text">
          {{ answer.text }}
        </p>
        <div
          v-if="answer.rationale"
          class="answer-rationale"
          :class="{ locked: rationalesLocked }"
          v-html="answer.rationale"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@/components/singletons/icon';
import MatrixGridInput from '@/components/flashcards/fragments/matrix-grid-input';

import { checkMatrixGridAnswerCorrectness, checkMatrixGridSelectionSelected, checkMatrixGridSelectionCorrectness } from '@/utils/flashcards';

export default {
  name: 'MatrixGridTable',
  components: { Icon, MatrixGridInput },
  props: {
    answers: {
      type: Array,
    },
    answered: {
      type: Boolean,
    },
    columns: {
      type: Number,
    },
    flashcard: {
      type: Object,
    },
    inputType: {
      type: String,
    },
    headers: {
      type: Array,
    },
    matrixGridSelections: {
      type: Array,
    },
    rationalesLocked: {
      type: Boolean,
    },
  },
  emits: ['handle-answer'],
  methods: {
    answerIconName(answerId) {
      const correct = this.isAnswerCorrect(answerId);
      return correct ? 'check' : 'cross-thin';
    },
    handleAnswer(params) {
      this.$emit('handle-answer', params);
    },
    isAnswerCorrect(answerId) {
      return checkMatrixGridAnswerCorrectness(this.flashcard, this.matrixGridSelections, answerId);
    },
    isSelectionCorrect(answerId, selectionIndex) {
      const correctness = checkMatrixGridSelectionCorrectness(this.flashcard, this.matrixGridSelections, answerId, selectionIndex);
      return correctness === 'correct';
    },
    isSelectionIncorrect(answerId, selectionIndex) {
      const correctness = checkMatrixGridSelectionCorrectness(this.flashcard, this.matrixGridSelections, answerId, selectionIndex);
      return correctness === 'incorrect';
    },
    isSelectionSelected(answerId, selectionIndex) {
      return checkMatrixGridSelectionSelected(this.matrixGridSelections, answerId, selectionIndex);
    },
  },
};
</script>

<style lang="scss">
.matrix-grid-table-component {
  $border-color: #d0d2d6;

  @include breakpoint(small) {
    display: none;
  }

  .matrix-grid-table {
    border: 1px solid $border-color;
    border-radius: 4px;
    margin: rem-calc(32) rem-calc(-48);

    .matrix-grid-row {
      border-bottom: 1px solid $border-color;
      display: flex;
      font-size: rem-calc(14);

      &:nth-child(2n) {
        background-color: #ededef;
      }

      &:last-child {
        border-color: transparent;
      }

      &.header {
        font-weight: $bold;
      }

      .matrix-grid-cell {
        align-items: center;
        box-sizing: border-box;
        border-right: 1px solid $border-color;
        display: flex;
        flex: 1;

        &:not(:first-child) {
          justify-content: center;
        }

        &:last-child {
          border-color: transparent;
        }

        .cell-text {
          margin: 16px;
        }

        .matrix-grid-input {
          height: 100%;
          width: 100%;

          .answer-label {
            align-items: center;
            display: flex;
            height: 100%;
            justify-content: center;
            width: 100%;

            &.selected {
              background-color: #e5fbfe;
            }
          }
        }
      }
    }

    &:not(.answered) {
      .matrix-grid-row {
        .matrix-grid-cell {
          .matrix-grid-input {
            .answer-label {
              &:hover {
                background-color: #e5fbfe;
              }
            }
          }
        }
      }
    }
  }

  .matrix-grid-table-answer-rationales {
    .answer-rationale-container {
      margin-top: rem-calc(48);

      &:last-child {
        margin-bottom: rem-calc(48);
      }

      .answer-icon-container {
        align-items: center;
        border-radius: 50%;
        display: inline-flex;
        height: 20px;
        justify-content: center;
        margin-right: 8px;
        vertical-align: middle;
        width: 20px;

        &.correct {
          background-color: #00b866;

          .answer-icon {
            height: 12px;
            width: 12px;
          }
        }

        &:not(.correct) {
          background-color: #ee1300;

          .answer-icon {
            height: 8px;
            width: 8px;
          }
        }
      }

      .answer-text {
        display: inline;
        font-weight: $bold;
        line-height: 1.5;
        margin: 0;
        vertical-align: middle;
      }

      .answer-rationale {
        line-height: 1.5;

        &.locked {
          filter: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg"><filter id="filter"><feGaussianBlur stdDeviation="5" /></filter></svg>#filter');
          filter: blur(4px);
        }
      }
    }
  }
}
</style>
